import {
  FEEDBACK_TYPE_IDS,
  MARKS_OUT_OF_MAX,
  MARKS_OUT_OF_MIN,
  isFeedbackTypeVisibleForContext,
} from './constants.js';
import { matchTools } from './toolMatchEngine.js';
import { parseTitleMarkRanges } from './toolVocabulary.js';
import { hasStimulus } from '../../Stimulus/stimulusHelpers';

// Tool types the recommendation engine manages. Self-assessment areas are
// deliberately excluded — they are reflective prompts, not marking templates,
// so auto-assigning them makes no sense.
export const RECOMMENDABLE_TOOL_TYPES = [
  FEEDBACK_TYPE_IDS.COMMENT_BANKS,
  FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA,
  FEEDBACK_TYPE_IDS.MARKING_TEMPLATES,
  FEEDBACK_TYPE_IDS.SHORT_ANSWERS,
];

/** All mark values mentioned in a title (range endpoints included). */
export const parseTitleMarks = (title) =>
  parseTitleMarkRanges(title).flatMap((r) =>
    r.min === r.max ? [r.min] : [r.min, r.max]
  );

/**
 * True when the question carries stimulus material. Delegates to the shared
 * stimulus helper: stimulusText holds Quill HTML, and a cleared editor
 * leaves markup like `<p><br></p>` behind — a plain truthiness check would
 * read that as a stimulus.
 */
export const questionHasStimulus = (question) => hasStimulus(question);

/**
 * Compute recommended marking tools for a question.
 *
 * context: { subjects: [codes], state, years, taskType, responseType,
 *            marksOutOf: number|null, hasStimulus, questionText,
 *            introParagraphs, bodyParagraphs, conclusionParagraphs }
 *
 * Returns a map keyed by feedback-type id; each value is `{ tools: [...] }`
 * or null when the slot does not apply. Selection is delegated to the
 * filter-and-relax cascade in toolMatchEngine.js, which always returns the
 * closest available tool for every applicable slot — a non-empty pool never
 * produces an empty recommendation.
 */
export const recommendMarkingTools = ({
  commentBanks = [],
  markingCriterias = [],
  successCriteria = [],
  shortAnswers = [],
  context = {},
}) => {
  const recommendations = {
    [FEEDBACK_TYPE_IDS.COMMENT_BANKS]: null,
    [FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA]: null,
    [FEEDBACK_TYPE_IDS.MARKING_TEMPLATES]: null,
    [FEEDBACK_TYPE_IDS.SHORT_ANSWERS]: null,
  };

  // The task type alone is enough to work with — AU generic subjects define
  // "Short Answer" task types with NO response types at all, and those
  // questions still deserve tools.
  if (!context.responseType && !context.taskType) return recommendations;

  const result = matchTools(
    { commentBanks, markingCriterias, successCriteria, shortAnswers },
    context
  );

  if (result.commentBank) {
    recommendations[FEEDBACK_TYPE_IDS.COMMENT_BANKS] = {
      tools: [result.commentBank],
    };
  }
  if (result.rubric) {
    recommendations[FEEDBACK_TYPE_IDS.MARKING_TEMPLATES] = {
      tools: [result.rubric],
    };
  }
  if (result.successCriteria && result.successCriteria.length > 0) {
    recommendations[FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA] = {
      tools: result.successCriteria,
    };
  }
  if (result.shortAnswer) {
    recommendations[FEEDBACK_TYPE_IDS.SHORT_ANSWERS] = {
      tools: [result.shortAnswer],
    };
  }
  return recommendations;
};

const hasSelection = (form, type) => {
  switch (type) {
    case FEEDBACK_TYPE_IDS.COMMENT_BANKS:
      return Boolean(form.commentBankId);
    case FEEDBACK_TYPE_IDS.MARKING_TEMPLATES:
      return Boolean(
        form.markingCriteria && Object.keys(form.markingCriteria).length > 0
      );
    case FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA:
      return (form.successCriteriaIds || []).length > 0;
    case FEEDBACK_TYPE_IDS.SHORT_ANSWERS:
      return (form.shortAnswerIds || []).length > 0;
    default:
      return false;
  }
};

const selectionEquals = (form, type, tools) => {
  const ids = tools.map((t) => t?.id);
  switch (type) {
    case FEEDBACK_TYPE_IDS.COMMENT_BANKS:
      return form.commentBankId === ids[0];
    case FEEDBACK_TYPE_IDS.MARKING_TEMPLATES:
      return form.markingCriteria?.id === ids[0];
    case FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA:
      return (form.successCriteriaIds || []).join('|') === ids.join('|');
    case FEEDBACK_TYPE_IDS.SHORT_ANSWERS:
      return (form.shortAnswerIds || []).join('|') === ids.join('|');
    default:
      return false;
  }
};

const selectionFields = (type, tools) => {
  switch (type) {
    case FEEDBACK_TYPE_IDS.COMMENT_BANKS:
      return {
        commentBankId: tools[0]?.id || '',
        commentBank: tools[0] || null,
      };
    case FEEDBACK_TYPE_IDS.MARKING_TEMPLATES:
      return { markingCriteria: tools[0] || null };
    case FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA:
      return { successCriteriaIds: tools.map((t) => t.id) };
    case FEEDBACK_TYPE_IDS.SHORT_ANSWERS:
      return { shortAnswerIds: tools.map((t) => t.id) };
    default:
      return {};
  }
};

const clearedFields = (type) => {
  switch (type) {
    case FEEDBACK_TYPE_IDS.COMMENT_BANKS:
      return { commentBankId: '', commentBank: null };
    case FEEDBACK_TYPE_IDS.MARKING_TEMPLATES:
      return { markingCriteria: null };
    case FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA:
      return { successCriteriaIds: [] };
    case FEEDBACK_TYPE_IDS.SHORT_ANSWERS:
      return { shortAnswerIds: [] };
    default:
      return {};
  }
};

/**
 * Merge engine recommendations into the question form state.
 *
 * Provenance rules (formData.toolAssignments[type]):
 *   - 'manual': the teacher chose (or explicitly removed) this tool type —
 *     the engine never touches it again.
 *   - 'auto': the current selection came from the engine — it may be
 *     replaced or cleared as settings change.
 *   - undefined + empty selection: free for the engine to fill.
 *
 * `autoEnabledToolTypes` records feedback-type toggles the ENGINE switched
 * on, so it only ever switches off toggles it owns.
 *
 * Returns `prev` unchanged (same reference) when nothing needs to change,
 * so the caller can use it directly inside a setState updater.
 */
export const applyRecommendationsToForm = (
  prev,
  recommendations,
  visibleTypes
) => {
  let feedbackTypes = [...(prev.feedbackTypes || [])];
  const assignments = { ...(prev.toolAssignments || {}) };
  let autoEnabled = [...(prev.autoEnabledToolTypes || [])];
  const updates = {};
  let changed = false;

  RECOMMENDABLE_TOOL_TYPES.forEach((type) => {
    if (!visibleTypes.includes(type)) {
      // Task/response type changed and this tool type no longer applies.
      // Withdraw anything the engine owns — otherwise an auto-assigned tool
      // would silently survive hidden in the form and get persisted on save
      // (only responseType changes clear values upstream; taskType-driven
      // visibility flips clear nothing). Manual/legacy values keep the
      // pre-existing hidden-but-kept behaviour; we only reset their flags.
      if (assignments[type] === 'auto') {
        Object.assign(updates, clearedFields(type));
        delete assignments[type];
        if (autoEnabled.includes(type)) {
          feedbackTypes = feedbackTypes.filter((t) => t !== type);
        }
        autoEnabled = autoEnabled.filter((t) => t !== type);
        changed = true;
        return;
      }
      if (assignments[type]) {
        delete assignments[type];
        changed = true;
      }
      if (autoEnabled.includes(type)) {
        autoEnabled = autoEnabled.filter((t) => t !== type);
        changed = true;
      }
      return;
    }

    const mode = assignments[type];
    if (mode === 'manual') return;

    const rec = recommendations[type];
    if (rec && rec.tools.length > 0) {
      if (mode !== 'auto' && hasSelection(prev, type)) return;
      if (!selectionEquals(prev, type, rec.tools)) {
        Object.assign(updates, selectionFields(type, rec.tools));
        changed = true;
      }
      if (assignments[type] !== 'auto') {
        assignments[type] = 'auto';
        changed = true;
      }
      if (!feedbackTypes.includes(type)) {
        feedbackTypes = [...feedbackTypes, type];
        if (!autoEnabled.includes(type)) autoEnabled = [...autoEnabled, type];
        changed = true;
      }
    } else if (mode === 'auto') {
      // The previous recommendation no longer holds (e.g. the pool shrank
      // and nothing matches the new settings): withdraw it.
      Object.assign(updates, clearedFields(type));
      delete assignments[type];
      if (autoEnabled.includes(type)) {
        feedbackTypes = feedbackTypes.filter((t) => t !== type);
        autoEnabled = autoEnabled.filter((t) => t !== type);
      }
      changed = true;
    }
  });

  if (!changed) return prev;
  return {
    ...prev,
    ...updates,
    feedbackTypes,
    toolAssignments: assignments,
    autoEnabledToolTypes: autoEnabled,
  };
};

/**
 * The marks value the engine should match against: only when marks are shown
 * and the value is a valid in-range integer, else null (no marks signal).
 */
export const resolveRecommendationMarks = (showMarks, marksOutOf) => {
  if (!showMarks) return null;
  const parsed = Number(marksOutOf);
  return Number.isInteger(parsed) &&
    parsed >= MARKS_OUT_OF_MIN &&
    parsed <= MARKS_OUT_OF_MAX
    ? parsed
    : null;
};

const TOOL_VALUE_FIELDS = {
  [FEEDBACK_TYPE_IDS.COMMENT_BANKS]: ['commentBankId', 'commentBank'],
  [FEEDBACK_TYPE_IDS.MARKING_TEMPLATES]: ['markingCriteria'],
  [FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA]: ['successCriteriaIds'],
  [FEEDBACK_TYPE_IDS.SHORT_ANSWERS]: ['shortAnswerIds'],
};

// Question fields that feed the recommendation context. A patch touching any
// of these re-runs the engine: task/response type and marks change which
// tools apply at all; stimulus, paragraph counts, and the question text
// drive the nuance tie-breaks (with/without-stimulus variants, single vs
// multiple body paragraphs, compare-style criteria).
const SETTINGS_FIELDS = [
  'taskType',
  'responseType',
  'showMarks',
  'marksOutOf',
  'stimulusText',
  'stimulusFiles',
  'introParagraphs',
  'bodyParagraphs',
  'conclusionParagraphs',
  'question',
];

const selectionKey = (form, type) => {
  switch (type) {
    case FEEDBACK_TYPE_IDS.COMMENT_BANKS:
      return form.commentBankId || '';
    case FEEDBACK_TYPE_IDS.MARKING_TEMPLATES:
      return form.markingCriteria?.id || '';
    case FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA:
      return (form.successCriteriaIds || []).join('|');
    case FEEDBACK_TYPE_IDS.SHORT_ANSWERS:
      return (form.shortAnswerIds || []).join('|');
    default:
      return '';
  }
};

/**
 * Merge an inline edit (question card, marking-rubrics modal, task-type
 * dropdowns, marks input) into a question, keeping recommendation state
 * consistent:
 *
 * 1. A patch that CHANGES a tool selection is a teacher decision — that tool
 *    type is flagged 'manual'. Patches that merely echo the current value
 *    (the rubrics modal saves every field) do not flag anything.
 * 2. A patch that touches the question settings (task/response type, marks,
 *    stimulus, paragraph counts, question text) re-runs the recommendation
 *    engine so auto-assigned tools immediately follow the new settings,
 *    under the usual provenance rules.
 *
 * `tools` is `{ commentBanks, markingCriterias, successCriteria,
 * shortAnswers }`; `context` is `{ subjects, state, years }`.
 */
export const applyQuestionPatch = (question, updates, { tools, context }) => {
  const next = { ...question, ...updates };
  const assignments = { ...(question.toolAssignments || {}) };
  let autoEnabled = [...(question.autoEnabledToolTypes || [])];

  RECOMMENDABLE_TOOL_TYPES.forEach((type) => {
    const touched = TOOL_VALUE_FIELDS[type].some((field) =>
      Object.prototype.hasOwnProperty.call(updates, field)
    );
    if (touched && selectionKey(question, type) !== selectionKey(next, type)) {
      assignments[type] = 'manual';
      autoEnabled = autoEnabled.filter((t) => t !== type);
    }
  });

  next.toolAssignments = assignments;
  next.autoEnabledToolTypes = autoEnabled;

  const settingsTouched = SETTINGS_FIELDS.some((field) =>
    Object.prototype.hasOwnProperty.call(updates, field)
  );
  if (!settingsTouched || (!next.responseType && !next.taskType)) return next;

  const recommendations = recommendMarkingTools({
    ...tools,
    context: {
      ...context,
      taskType: next.taskType,
      responseType: next.responseType,
      marksOutOf: resolveRecommendationMarks(next.showMarks, next.marksOutOf),
      hasStimulus: questionHasStimulus(next),
      questionText: next.question,
      introParagraphs: next.introParagraphs,
      bodyParagraphs: next.bodyParagraphs,
      conclusionParagraphs: next.conclusionParagraphs,
    },
  });
  const visibleTypes = RECOMMENDABLE_TOOL_TYPES.filter((type) =>
    isFeedbackTypeVisibleForContext(type, {
      responseType: next.responseType,
      taskType: next.taskType,
    })
  );
  return applyRecommendationsToForm(next, recommendations, visibleTypes);
};

/**
 * Apply an engine patch to the question matching `serialNumber` within a flat
 * questions array, leaving the others untouched. Top-level questions and the
 * sub-questions of a multi-part question are treated identically — both run
 * the recommendation engine — so marking tools auto-assign no matter which
 * surface the teacher edits. `inputs` is `{ tools, context }` (see
 * applyQuestionPatch).
 */
export const patchQuestionsWithRecommendations = (
  questions,
  serialNumber,
  updates,
  inputs
) =>
  (questions || []).map((q) =>
    String(q.serialNumber) === String(serialNumber)
      ? applyQuestionPatch(q, updates, inputs)
      : q
  );

/**
 * Provenance flags for a question loaded into the edit dialog. Questions
 * saved before this feature (or round-tripped through the backend, which
 * does not store the flags) get every existing selection marked 'manual'
 * so re-editing never clobbers what a teacher previously saved.
 */
export const deriveInitialToolAssignments = (question) => {
  if (
    question?.toolAssignments &&
    typeof question.toolAssignments === 'object'
  ) {
    return { ...question.toolAssignments };
  }
  const assignments = {};
  RECOMMENDABLE_TOOL_TYPES.forEach((type) => {
    if (hasSelection(question || {}, type)) assignments[type] = 'manual';
  });
  return assignments;
};
